import { useEffect } from 'react';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { traceModuleEvaluation } from '@/utils/moduleTrace';
import { TripErrorBoundary } from '@/utils/errorBoundary';
import { TripMapExperience } from '@/features/maps/TripMapExperience';
import { useTripSelectionStore } from '@/state/tripSelectionStore';
import { normalizeTripRouteParam } from '@/utils/tripSelection';

traceModuleEvaluation('app/(tabs)/maps');

type MapsScreenProps = {
  hideHeader?: boolean;
  isActive?: boolean;
  tripId?: string | null;
  focusLegId?: string | null;
};

export default function MapsScreen({
  hideHeader = false,
  isActive = true,
  tripId: tripIdProp,
  focusLegId: focusLegIdProp,
}: MapsScreenProps = {}) {
  const params = useLocalSearchParams();
  const router = useRouter();
  const rememberedTripId = useTripSelectionStore((state) => state.lastViewedTripId);
  const rememberTripId = useTripSelectionStore((state) => state.rememberTripId);
  const paramTripId = normalizeTripRouteParam(tripIdProp ?? params.tripId);
  // Match Plan tab: deep link/query wins; otherwise stay aligned with globally remembered selection.
  const effectiveTripId = paramTripId ?? rememberedTripId ?? null;
  const openOffline =
    params.openOffline === '1' ||
    params.openOffline === 'true';
  const focusLegId =
    focusLegIdProp ??
    (typeof params.focusLegId === 'string' ? params.focusLegId : undefined);

  useEffect(() => {
    if (effectiveTripId) {
      rememberTripId(effectiveTripId);
    }
  }, [effectiveTripId, rememberTripId]);

  return (
    <TripErrorBoundary name="MapsScreen">
      <TripMapExperience
        hideHeader={hideHeader}
        isActive={isActive}
        initialTripId={effectiveTripId}
        controlledTripId={effectiveTripId}
        openOfflineOnLoad={!!openOffline}
        focusLegId={focusLegId}
        onTripChange={(tripId) => {
          rememberTripId(tripId);
          try {
            router.setParams({ tripId: tripId ?? undefined });
          } catch (error) {
            // Router not ready yet, will sync on next render
            console.debug('[Maps] Router not ready for setParams');
          }
        }}
      />
    </TripErrorBoundary>
  );
}
